import { Component, Input, EventEmitter, Output, QueryList, ViewChildren, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService, ToastType } from '../../../shared/toast/toast.service';
import { DatetimePickerComponent } from '../../../shared/datetime-picker/datetime-picker.component';
import { ReportConfiguration } from '../../models/report-configuration';
import { ReportRunParameter } from '../../models/reports-reportsrunparameters';
import { ReportsResult } from '../../models/reports-reportsresult';
import { ReportsService } from '../../services/reports.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';

@Component({
  selector: 'app-reports-run-report-configuration',
  templateUrl: './reports-run-report-configuration.component.html',
  styleUrls: ['./reports-run-report-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, DatetimePickerComponent]
})
export class ReportsRunReportConfigurationComponent implements OnInit {
  private readonly reportsService = inject(ReportsService);
  private readonly loadingPanelService = inject(LoadingPanelService);
  private readonly toastService = inject(ToastService);

  @ViewChildren('dateparam') private dateParams!: QueryList<DatetimePickerComponent>;
  @Input() reportId = 0;
  @Output() readonly resultOutput = new EventEmitter<ReportsResult>();

  private readonly textParameterTypes = new Set<string>(['nvarchar(100)', 'nvarchar(500)', 'nvarchar(max)']);
  private invalidDateDetected = false;
  reportConfiguration: ReportConfiguration = null;

  ngOnInit(): void {
    void this.refresh();
  }

  getParameterTypeKey(type: string | null | undefined): 'bit' | 'int' | 'datetime' | 'text' | 'unsupported' {
    const normalizedType = (type ?? '').toLowerCase();
    if (normalizedType === 'bit' || normalizedType === 'int' || normalizedType === 'datetime') {
      return normalizedType;
    }
    if (this.textParameterTypes.has(normalizedType)) {
      return 'text';
    }
    return 'unsupported';
  }

  toBool(value: string | null | undefined): boolean {
    return !this.isBitFalse(value);
  }

  get isValid(): boolean {
    if (this.dateParams) {
      let anyInvalid = false;
      this.dateParams.toArray().forEach(element => {
        const valid = element.datepicker.instance.option('isValid');
        if (!valid) {
          anyInvalid = true;
          this.invalidDateDetected = true;
        }
      });
      if (!anyInvalid) {
        this.invalidDateDetected = false;
      }
    }

    let dataValidated = true;
    if (!this.reportConfiguration?.ReportParameters) {
      return false;
    }
    if (this.invalidDateDetected) {
      return false;
    }
    this.reportConfiguration.ReportParameters.forEach(element => {
      if (element.Type.toLowerCase() === 'int' && (element.DefaultValue === '' || element.DefaultValue === null)) {
        dataValidated = false;
      }
      if (element.Type.toLowerCase() === 'datetime' && (element.DefaultValue === '' || element.DefaultValue === null)) {
        dataValidated = false;
      }
    });
    this.invalidDateDetected = false;
    return dataValidated;
  }

  async runReport(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');

      const parameters: ReportRunParameter[] = this.reportConfiguration?.ReportParameters?.map(element => (
        { Name: element.Name, Value: this.getParameterValue(element.Type, element.DefaultValue), Type: element.Type }
      )) ?? [];

      const reportResult = await this.reportsService.runReport(this.reportId, parameters);
      this.resultOutput.emit(reportResult);
    }
    catch {
      this.toastService.showToast('Failed to load data...', ToastType.error);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  async refresh(): Promise<void> {
    this.reportConfiguration = (await this.reportsService.getReportConfigurations(this.reportId))[0] ?? null;
  }

  private getParameterValue(type: string, defaultValue: string | null | undefined): string {
    switch (this.getParameterTypeKey(type)) {
      case 'bit':
        return this.isBitFalse(defaultValue) ? 'FALSE' : 'TRUE';
      case 'datetime':
        return this.toIsoString(defaultValue);
      default:
        return String(defaultValue ?? ''); // Ensure it's always a string.
    }
  }

  private isBitFalse(value: string | null | undefined): boolean {
    return !value || value === '0' || value?.toLowerCase() === 'false';
  }

  private toIsoString(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
  }
}
